import {
  SingleExpression,
  ConstraintPart,
  OtherConstraintPart,
  Filter,
  Select,
  Query,
} from '@/models/custom-metric.model'

export function getConstraintPart(
  value: string,
  title?: string,
  uuid?: string,
): Array<OtherConstraintPart> {
  switch (value) {
    case 'attribute':
      return [
        {
          title: title && isNaN(Number(title)) ? title : 'Attribute',
          value,
          icon: 'mdi-card-bulleted',
          uuid,
        },
      ]
    case 'property':
      return [
        {
          title: title && isNaN(Number(title)) ? title : 'Attribute',
          value,
          icon: 'mdi-card-bulleted',
          uuid,
        },
      ]
    case 'eq':
      return [{ title: 'Equals', value, icon: 'mdi-equal', uuid }]
    case 'ne':
      return [{ title: 'Not equals', value, icon: 'mdi-not-equal', uuid }]
    case 'operator':
      return [...getConstraintPart('eq', uuid), ...getConstraintPart('ne', uuid)]
    case 'literal':
    default:
      return []
  }
}

export function removeExtraToolboxAssistFields(query: Query): Query {
  if (!query || !query.select) {
    return query
  }

  const newQuery: Query = JSON.parse(JSON.stringify(query))

  let assistFieldFound = false

  newQuery.select = newQuery.select.filter((item) => {
    if (item.field && item.field.type === 'toolbox.assist') {
      if (!assistFieldFound) {
        assistFieldFound = true
        return true
      }
      return false
    }
    return true
  })

  return newQuery
}

export function hasToolboxAssistConstraint(query: Query): boolean {
  if (query.from.type == 'traces') {
    if (!query.from.parameters) {
      return false
    }

    return query.from.parameters.some((constraint) => {
      if (constraint.type === 'eq')
        return constraint.right && constraint.right.type === 'toolbox.assist'
    })
  }
  return false
}

export function countToolboxAssistParameters(query: Query): number {
  if (query.from.type == 'traces') {
    if (!query.from.parameters) {
      return 0
    }

    return query.from.parameters.reduce((count, constraint) => {
      if (
        constraint.type === 'eq' &&
        constraint.right &&
        constraint.right.type === 'toolbox.assist'
      ) {
        return count + 1
      }
      return count
    }, 0)
  }
  return 0
}

export function removeAllToolBoxAssistFieldsFromSelect(query: Query): Query {
  if (!query || !query.select) {
    return query
  }

  const newQuery: Query = JSON.parse(JSON.stringify(query))

  newQuery.select = newQuery.select.filter((item) => item.field.type !== 'toolbox.assist')

  return newQuery
}

export function hasToolboxAssistField(query: Query): boolean {
  if (!query || !query.select) {
    return false
  }

  return query.select.some((item) => item.field && item.field.type === 'toolbox.assist')
}

export function hasToolboxAssist(queryElement: any): boolean {
  return queryElement && JSON.stringify(queryElement).indexOf('toolbox.assist') >= 0
}

export function containsToolboxAssistFieldInFilter(filter: Filter): boolean {
  if (filter.type === 'toolbox.assist') {
    return true
  }

  if (
    (filter.type === 'eq' || filter.type === 'and' || filter.type === 'ne') &&
    'left' in filter &&
    'right' in filter
  ) {
    return (
      containsToolboxAssistFieldInFilter(filter.left) ||
      containsToolboxAssistFieldInFilter(filter.right)
    )
  }

  return false
}

/*finds and returns the artifactType name in the whole filter of the custom metric query.*/
export function findArtifactTypeNameInQueryFilter(filter: Filter): string {
  if (
    filter &&
    filter.type === 'eq' &&
    filter.left &&
    filter.left.type === 'field' &&
    filter.left.field.type === 'artifact.property' &&
    filter.left.field.property === 'artifact.type.name' &&
    filter.right &&
    filter.right.type === 'literal'
  ) {
    return filter.right.value
  }
  if (filter && filter.type === 'and') {
    const leftValue = filter.left ? findArtifactTypeNameInQueryFilter(filter.left) : null
    const rightValue = filter.right ? findArtifactTypeNameInQueryFilter(filter.right) : null

    if (leftValue) return leftValue
    if (rightValue) return rightValue
  }
  return ''
}

// returns the Select field with the specific name
export function getSelectFromQuery(query: Query, name: string): Select | undefined {
  const select = query.select.find((select) => select.as == name)
  return select
}

// gets the literal of a singleExpression (which is the right side value)
export function getLiteralForFilterExpression(expression: SingleExpression) {
  return expression.right.value
}

// gets the operator of a singleExpression
export function getOperatorForFilterExpression(expression: SingleExpression) {
  return expression.type
}

/* converts a SingleExpression Object into a constraintParts array so it can be
  splitted into the Filter edit text field
*/
export function expressionToConstraintParts(expression: Filter | undefined): ConstraintPart[] {
  if (!expression) {
    return []
  }

  const filterPartsFn: (expression: Filter) => ConstraintPart[] = (expression: Filter) => {
    switch (expression.type) {
      case 'ne':
        return [
          ...filterPartsFn(expression.left),
          { value: 'ne', title: 'ne', icon: 'mdi-not-equal', uuid: expression.uuid },
          ...filterPartsFn(expression.right),
        ]
      case 'eq':
        return [
          ...filterPartsFn(expression.left),
          { value: 'eq', title: 'eq', icon: 'mdi-equal', uuid: expression.uuid },
          ...filterPartsFn(expression.right),
        ]
      case 'and':
        return [
          ...filterPartsFn(expression.left),
          {
            value: 'eq' /*and not defined yet */,
            title: 'and',
            icon: 'mdi-and',
            uuid: expression.uuid,
          },
          ...filterPartsFn(expression.right),
        ]
      case 'toolbox.assist':
        return [
          {
            value: 'literal' /* no type for assist */,
            literal: `Assist`,
            icon: 'mdi-card-bulleted',
            uuid: expression.uuid,
          },
        ]
      case 'field': {
        if (expression.field.type === 'artifact.attribute') {
          return [
            {
              value: 'attribute',
              title: `${expression.field.attribute}(${expression.field.index})`,
              icon: 'mdi-card-bulleted',
              uuid: expression.uuid,
            },
          ]
        } else if (expression.field.type === 'artifact.property') {
          return [
            {
              value: 'property',
              title: `${expression.field.property}(${expression.field.index})`,
              icon: 'mdi-card-bulleted',
              uuid: expression.uuid,
            },
          ]
        } else {
          return [
            {
              value: 'literal' /* no type for assist */,
              literal: `Field assist (${expression.field.index})`,
              icon: 'mdi-card-bulleted',
              uuid: expression.uuid,
            },
          ]
        }
      }
      case 'literal':
        return [{ value: 'literal', literal: expression.value, uuid: expression.uuid }]

      default:
        return []
    }
  }

  // const operatorPartsFn: (expression: Expression)=>ConstraintPart[] = (expression: Expression) => {switch (expression.type){
  //   case 'ne': return [...filterPartsFn(expression.left), { value: 'ne', title: 'ne', icon: 'mdi-not-equal', uuid: expression.uuid }, ...filterPartsFn(expression.right)];
  //   case 'eq': return [...filterPartsFn(expression.left), { value: 'eq', title: 'eq', icon: 'mdi-equal', uuid: expression.uuid }, ...filterPartsFn(expression.right)];
  //   case 'and': return [...filterPartsFn(expression.left), { value: 'eq' /*and not defined yet */, title: 'and', icon: 'mdi-and', uuid: expression.uuid }, ...filterPartsFn(expression.right)];
  //   case 'artifact.attribute': return [{ value: 'attribute', title: `${expression.attribute}(${expression.index})`, icon: 'mdi-card-bulleted', uuid: expression.uuid}]
  //   case 'artifact.property': return [{ value: 'property', title: `${expression.property}(${expression.index})`, icon: 'mdi-card-bulleted', uuid: expression.uuid }]
  //   case 'literal': return [{ value: 'literal', literal: expression.value, uuid: expression.uuid }];

  //   default: return []
  //   }
  // }
  return filterPartsFn(expression)
}

/* converts a SingleExpression Object into a constraintParts array so it can be
  splitted into the Filter edit text field
*/
export function singleExpressionToConstraintParts(
  expression: SingleExpression | undefined,
): ConstraintPart[] {
  if (!expression) {
    return []
  }

  const operatorParts = getConstraintPart(expression.type)[0] // Assuming 'eq', 'ne', etc.
  let leftParts: ConstraintPart[] = []
  let rightParts: ConstraintPart[] = []

  if (expression.left.type === 'artifact.attribute') {
    leftParts = getConstraintPart('attribute', expression.left.attribute)
  }

  if (expression.left.type === 'artifact.property') {
    leftParts = getConstraintPart('property', expression.left.property)
  }

  rightParts = [expression.right.value]

  return [...leftParts, operatorParts, ...rightParts]
}
